/**
 * Image conversion utilities.
 * Converts files to various image formats using canvas.
 *
 * @module image-converter
 */

// Declare external dependencies (loaded from CDN or bundled separately)
declare function loadImage(file: File, callback?: (canvas: HTMLCanvasElement) => void, options?: Record<string, unknown>): void;
declare function dataURLtoBlob(dataUrl: string): Blob;

/**
 * Convert a file to a JPG image.
 * Uses canvas to render and convert the image.
 * @param file - The source file (image)
 * @returns Promise resolving to an HTMLImageElement
 */
export function fileToJpgImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        try {
            loadImage(file, (canvas: HTMLCanvasElement) => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.onerror = (err) => reject(err);
                image.src = URL.createObjectURL(dataURLtoBlob(canvas.toDataURL('image/jpeg', 1.0)));
            }, {
                canvas: true,
            });
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * Convert a file to a PNG image with transparency support.
 * Uses canvas to render and convert the image.
 * @param file - The source file (image)
 * @returns Promise resolving to an HTMLImageElement
 */
export function fileToTransparentPngImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        try {
            loadImage(file, (canvas: HTMLCanvasElement) => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.onerror = (err) => reject(err);
                image.src = URL.createObjectURL(dataURLtoBlob(canvas.toDataURL('image/png', 1.0)));
            }, {
                canvas: true,
            });
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * Convert a file to a WebP image.
 * Uses canvas to render and convert the image.
 * @param file - The source file (image)
 * @param quality - Quality setting (0-1), default 0.9
 * @returns Promise resolving to an HTMLImageElement
 */
export function fileToWebPImage(file: File, quality: number = 0.9): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        try {
            loadImage(file, (canvas: HTMLCanvasElement) => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.onerror = (err) => reject(err);
                image.src = URL.createObjectURL(dataURLtoBlob(canvas.toDataURL('image/webp', quality)));
            }, {
                canvas: true,
            });
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * Convert a file to a Blob of specified format.
 * @param file - The source file (image)
 * @param format - Target format ('image/jpeg', 'image/png', 'image/webp')
 * @param quality - Quality setting (0-1), default 1.0
 * @returns Promise resolving to a Blob
 */
export function fileToImageBlob(file: File, format: string, quality: number = 1.0): Promise<Blob> {
    return new Promise((resolve, reject) => {
        try {
            loadImage(file, (canvas: HTMLCanvasElement) => {
                const blob = dataURLtoBlob(canvas.toDataURL(format, quality));
                resolve(blob);
            }, {
                canvas: true,
            });
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * Get image dimensions from a file.
 * @param file - The image file
 * @returns Promise resolving to { width, height }
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        const url = URL.createObjectURL(file);

        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: image.naturalWidth, height: image.naturalHeight });
        };
        image.onerror = (err) => {
            URL.revokeObjectURL(url);
            reject(err);
        };

        image.src = url;
    });
}
